import { TimerIcon } from '@phosphor-icons/react';
import { Checkmark } from '@remarkable/ark-icons';
// eslint-disable-next-line no-restricted-imports
import { Button as ArkButton, Tag, Typography } from '@remarkable/ark-web';
import { getRouteApi } from '@tanstack/react-router';
import clsx from 'clsx';
import { ButtonClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import type {
  CloudConnectSubscription,
  Plan,
} from 'src/api/endpoints/cloudConnect.types';
import { useCreateBillingPortalSession } from 'src/api/queries/enterpriseSubscription';
import { useAccessTokenClaims } from 'src/api/queries/user';
import { Button, Modal, Tooltip } from 'src/components';
import { PriceConnect } from 'src/components/PriceConnect';
import { StatusTagEnterprise } from 'src/components/StatusTagEnterprise';
import { cn } from 'src/utils/classNamesHelper';
import { connectSubscriptionHelpers } from 'src/utils/connectSubscriptionHelpers';
import { formatDate } from 'src/utils/formatDate';
import { useFeatureFlags } from 'src/utils/useFeatureFlags';
import { userHelpers } from 'src/utils/userHelpers';
import { AbortSwitchToAnnualModal } from './AbortSwitchToAnnualModal';
import { AbortSwitchToMonthlyModal } from './AbortSwitchToMonthlyModal';
import { ChangePlanAnnualModal } from './ChangePlanAnnualModal';
import { ChangePlanMonthlyModal } from './ChangePlanMonthlyModal';

const billingRouteApi = getRouteApi(
  '/_auth/enterprise/_with-org/_layout/billing'
);

export function PlanCard({
  plan,
  subscription,
  className,
  ...props
}: {
  plan: Plan;
  subscription: CloudConnectSubscription;
} & React.HTMLAttributes<HTMLDivElement>) {
  const isActive = plan.interval === subscription.planCurrent.interval;
  const isUpcoming = subscription.planUpcoming?.interval === plan.interval;

  return (
    <div
      key={plan.name}
      data-cy={isActive ? 'current-plan' : 'plan-option'}
      {...props}
      className={cn('size-full', className)}
    >
      <div
        className={clsx(
          'flex flex-col justify-between gap-y-12 rounded-[4px] border p-20',
          isActive
            ? 'border-interaction-primary bg-interaction-primary-default-subtle'
            : 'border-paper-8 text-muted'
        )}
      >
        <div>
          <div className="flex items-center gap-16">
            <Typography variant="heading-subtle-2xl" className="my-0">
              {plan.interval === 'annual' && 'Annual plan'}
              {plan.interval === 'monthly' && 'Monthly plan'}
            </Typography>

            {isActive && (
              <StatusTagEnterprise
                subscription={subscription}
                fallback={
                  <Tag variant="success-light">
                    <Checkmark /> Active
                  </Tag>
                }
              />
            )}
            {isUpcoming && (
              <Tag variant="info-light">
                <TimerIcon /> Starts at{' '}
                {subscription.periodEnd
                  ? formatDate(subscription.periodEnd)
                  : 'period end'}
              </Tag>
            )}
          </div>
          <Typography variant="body-sm" className="mt-8">
            {plan.interval === 'annual' &&
              'Lower price per seat. Commit for a year and scale up as needed.'}
            {plan.interval === 'monthly' &&
              'Full flexibility. Cancel or adjust seats anytime.'}
          </Typography>
        </div>
        <div className="flex flex-col justify-between gap-24 ls:flex-row ls:items-center">
          <div>
            <Typography variant="body-lg">
              <PriceConnect
                price={plan.prices.monthly}
                variant="unit"
                itemLabel="seat"
                boldPrice
              />
            </Typography>
            <Typography variant="body-sm" className="my-0 text-muted">
              <PriceConnect
                price={plan.prices.annual}
                variant="unit"
                itemLabel="seat"
              />
            </Typography>
          </div>

          <PlanCardAction
            subscription={subscription}
            plan={plan}
            isActive={isActive}
            isUpcoming={isUpcoming}
          />
        </div>
      </div>
    </div>
  );
}

function PlanCardAction({
  subscription,
  plan,
  isActive,
  isUpcoming,
}: {
  subscription: CloudConnectSubscription;
  plan: Plan;
  isActive: boolean;
  isUpcoming: boolean;
} & React.HTMLAttributes<HTMLDivElement>) {
  const search = billingRouteApi.useSearch();
  const navigate = billingRouteApi.useNavigate();
  const billingPortalSession = useCreateBillingPortalSession();
  const featureFlags = useFeatureFlags();
  const accessTokenClaims = useAccessTokenClaims();

  const canSelectAnnualPlan =
    featureFlags.EnableEnterpriseAnnualPlan &&
    plan.interval === 'annual' &&
    !isActive &&
    !isUpcoming &&
    userHelpers.hasPermissionUpdateSubscription(accessTokenClaims.data);

  const canSelectMonthlyPlan =
    plan.interval === 'monthly' &&
    !isActive &&
    !isUpcoming &&
    userHelpers.hasPermissionUpdateSubscription(accessTokenClaims.data);

  const canAbortSwitchingToAnnualPlan =
    featureFlags.EnableEnterpriseAnnualPlan &&
    plan.interval === 'annual' &&
    subscription.planUpcoming?.interval === 'annual' &&
    userHelpers.hasPermissionUpdateSubscription(accessTokenClaims.data);

  const canAbortSwitchingToMonthlyPlan =
    plan.interval === 'monthly' &&
    subscription.planUpcoming?.interval === 'monthly' &&
    userHelpers.hasPermissionUpdateSubscription(accessTokenClaims.data);

  if (!userHelpers.hasPermissionUpdateSubscription(accessTokenClaims.data)) {
    return null;
  }

  if (isActive && connectSubscriptionHelpers.isOverdue(subscription)) {
    return (
      <ArkButton
        as="a"
        href={billingPortalSession.data?.url}
        size="medium"
        target="_blank"
        rel="noopener noreferrer"
        data-cy="update-payment-link-overdue"
        onClick={() =>
          tracker.trackEvent(
            new ButtonClicked({
              component_location: ComponentLocations.ENTERPRISE.BILLING_PAGE,
              text: 'Pay now',
              action: 'go to billing portal to pay overdue invoice',
            })
          )
        }
      >
        Pay now
      </ArkButton>
    );
  }

  if (canAbortSwitchingToAnnualPlan) {
    return (
      <Modal.Root
        open={!!search.abortSwitchToAnnualPlan}
        onOpenChange={(open) => {
          if (open) {
            void navigate({
              search: (prev) => ({
                ...prev,
                abortSwitchToAnnualPlan: 'confirm',
              }),
            });
          } else {
            void navigate({
              search: (prev) => ({
                ...prev,
                abortSwitchToAnnualPlan: undefined,
              }),
            });
          }
        }}
      >
        <Modal.Trigger asChild>
          <Button
            variant="tertiary"
            size="medium"
            data-cy="abort-switch-annual-plan-button"
            aria-label="Abort switching to annual plan"
            mobileFullWidth
            analytics={{
              text: 'Cancel switch',
              location: ComponentLocations.ENTERPRISE.BILLING_PAGE,
              action: 'open abort switch to annual plan modal',
            }}
          >
            Cancel switch
          </Button>
        </Modal.Trigger>

        <AbortSwitchToAnnualModal
          onSuccess={() => {
            void navigate({
              search: (prev) => ({
                ...prev,
                abortSwitchToAnnualPlan: undefined,
              }),
            });
          }}
        />
      </Modal.Root>
    );
  }

  if (canAbortSwitchingToMonthlyPlan) {
    return (
      <Modal.Root
        open={!!search.abortSwitchToMonthlyPlan}
        onOpenChange={(open) => {
          if (open) {
            void navigate({
              search: (prev) => ({
                ...prev,
                abortSwitchToMonthlyPlan: 'confirm',
              }),
            });
          } else {
            void navigate({
              search: (prev) => ({
                ...prev,
                abortSwitchToMonthlyPlan: undefined,
              }),
            });
          }
        }}
      >
        <Modal.Trigger asChild>
          <Button
            variant="tertiary"
            size="medium"
            data-cy="abort-switch-monthly-plan-button"
            aria-label="Abort switching to monthly plan"
            mobileFullWidth
            analytics={{
              text: 'Cancel switch',
              location: ComponentLocations.ENTERPRISE.BILLING_PAGE,
              action: 'open abort switch to monthly plan modal',
            }}
          >
            Cancel switch
          </Button>
        </Modal.Trigger>

        <AbortSwitchToMonthlyModal
          onSuccess={() => {
            void navigate({
              search: (prev) => ({
                ...prev,
                abortSwitchToMonthlyPlan: undefined,
              }),
            });
          }}
        />
      </Modal.Root>
    );
  }

  if (canSelectAnnualPlan) {
    return (
      <Modal.Root
        open={!!search.switchToAnnualPlan}
        onOpenChange={(open) => {
          if (open) {
            void navigate({
              search: (prev) => ({
                ...prev,
                switchToAnnualPlan: 'select-seats',
              }),
            });
          } else {
            void navigate({
              search: (prev) => ({
                ...prev,
                switchToAnnualPlan: undefined,
              }),
            });
          }
        }}
      >
        <Tooltip
          asChild
          enabled={connectSubscriptionHelpers.isOverdue(subscription)}
          title="Cannot switch plan while subscription is overdue"
        >
          <Modal.Trigger asChild>
            <Button
              variant="primary"
              size="medium"
              data-cy="select-annual-plan-button"
              aria-label="Switch to annual plan"
              mobileFullWidth
              disabled={connectSubscriptionHelpers.isOverdue(subscription)}
              analytics={{
                text: 'Select plan',
                location: ComponentLocations.ENTERPRISE.BILLING_PAGE,
                action: 'open switch to annual plan modal',
              }}
            >
              Select plan
            </Button>
          </Modal.Trigger>
        </Tooltip>

        <ChangePlanAnnualModal />
      </Modal.Root>
    );
  }

  if (canSelectMonthlyPlan) {
    return (
      <Modal.Root
        open={!!search.switchToMonthlyPlan}
        onOpenChange={(open) => {
          if (open) {
            void navigate({
              search: (prev) => ({
                ...prev,
                switchToMonthlyPlan: 'confirm',
              }),
            });
          } else {
            void navigate({
              search: (prev) => ({
                ...prev,
                switchToMonthlyPlan: undefined,
              }),
            });
          }
        }}
      >
        <Tooltip
          asChild
          enabled={connectSubscriptionHelpers.isOverdue(subscription)}
          title="Cannot switch plan while subscription is overdue"
        >
          <Modal.Trigger asChild>
            <Button
              variant="primary"
              size="medium"
              data-cy="select-monthly-plan-button"
              aria-label="Switch to monthly plan"
              mobileFullWidth
              disabled={connectSubscriptionHelpers.isOverdue(subscription)}
              analytics={{
                text: 'Select plan',
                location: ComponentLocations.ENTERPRISE.BILLING_PAGE,
                action: 'open switch to monthly plan modal',
              }}
            >
              Select plan
            </Button>
          </Modal.Trigger>
        </Tooltip>

        <ChangePlanMonthlyModal />
      </Modal.Root>
    );
  }

  return null;
}
